import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { AgentRegistryApi } from './AgentRegistryApi';
import type {
  AgentOut,
  AgentSchemaOut,
  DiscoveryQuery,
  DiscoveryResponse,
  DriftFlagOut,
} from './types';

export class AgentRegistryClient implements AgentRegistryApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxyUrl}/agent-gateway`;
  }

  async discoverAgents(query?: DiscoveryQuery): Promise<DiscoveryResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/agents/discover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(query ?? { query: '', limit: 50 }),
    });

    if (!response.ok) {
      throw new Error(`Failed to discover agents (HTTP ${response.status})`);
    }

    return await response.json();
  }

  async getAgent(name: string): Promise<AgentOut> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/${encodeURIComponent(name)}/latest`,
    );

    if (!response.ok) {
      throw new Error(`Agent '${name}' not found (HTTP ${response.status})`);
    }

    return await response.json();
  }

  async getAgentVersions(name: string): Promise<AgentOut[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/${encodeURIComponent(name)}/versions`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch versions for '${name}' (HTTP ${response.status})`);
    }

    return await response.json();
  }

  async getAgentSchema(name: string): Promise<AgentSchemaOut> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/${encodeURIComponent(name)}/schema`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch schema for '${name}' (HTTP ${response.status})`);
    }

    return await response.json();
  }

  async getAgentFlags(name: string): Promise<DriftFlagOut[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/agents/${encodeURIComponent(name)}/flags`,
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch drift flags for '${name}' (HTTP ${response.status})`);
    }

    return await response.json();
  }
}
